import {Button, Collapse, Space, message} from 'antd';
import {store} from 'store';
import {selectUserContext} from 'store/authSlice';
import {FETCH_FAILURE_MESSAGE_RE} from 'utils/consts';
import {buildSupportEmailMessage, sendSupportEmail} from 'utils/contactIT';
import {getDatadogRum} from 'utils/datadog';
import errorLogger, {
  createTrackError,
  extractRequestMeta,
  parseError,
} from 'utils/errors';
import {openLoginPopup} from 'utils/loginPopup';

/**
 * UI actions & labels
 */
export const ERROR_ACTIONS = {
  LOGIN: 'login',
  HOME: 'home',
  BACK: 'back',
  CLOSE: 'close',
  REPORT: 'report',
  RETRY: 'retry',
};
export const getActionLabel = (action) => {
  const labels = {
    [ERROR_ACTIONS.LOGIN]: 'Log In',
    [ERROR_ACTIONS.HOME]: 'Go Home',
    [ERROR_ACTIONS.BACK]: 'Back',
    [ERROR_ACTIONS.CLOSE]: 'Close',
    [ERROR_ACTIONS.REPORT]: 'Report',
    [ERROR_ACTIONS.RETRY]: 'Try Again',
  };
  return labels[action] || action;
};

export const SESSION_EXPIRED_MESSAGE_KEY =
  'Your session has expired. You will need to login again.';

/**
 * Strategies
 */
const strategies = {
  unauthenticated: () => ({
    title: 'Authentication Required',
    message: 'Please log in to continue.',
    actions: [ERROR_ACTIONS.LOGIN, ERROR_ACTIONS.HOME, ERROR_ACTIONS.REPORT],
  }),
  authentication: () => ({
    title: 'Session Expired',
    message: SESSION_EXPIRED_MESSAGE_KEY,
    actions: [ERROR_ACTIONS.LOGIN, ERROR_ACTIONS.HOME, ERROR_ACTIONS.REPORT],
  }),
  authorization: (error) => ({
    title: 'Access Denied',
    message:
      error.message ||
      'You do not have permission to access this application. Please contact IT Support.',
    actions: [ERROR_ACTIONS.LOGIN, ERROR_ACTIONS.HOME, ERROR_ACTIONS.REPORT],
  }),
  permissionDenied: () => ({
    title: 'Permission Denied',
    message:
      "You don't have permission to view this page. If you believe this is an error, please try to log in again. If the issue persists, please contact IT Support.",
    actions: [ERROR_ACTIONS.BACK, ERROR_ACTIONS.LOGIN, ERROR_ACTIONS.REPORT],
  }),
  notFound: (error) => ({
    title: 'Not Found',
    message: error.message || 'The requested resource was not found.',
    actions: [ERROR_ACTIONS.BACK, ERROR_ACTIONS.CLOSE],
  }),
  serverError: () => ({
    title: 'Server Error',
    message:
      'An error occurred on the server. Please try again or contact support if the issue persists.',
    actions: [ERROR_ACTIONS.REPORT, ERROR_ACTIONS.CLOSE],
  }),
  networkError: () => ({
    title: 'Connection Error',
    message:
      'Unable to connect to the server. Please check your internet connection and try again.',
    actions: [ERROR_ACTIONS.RETRY, ERROR_ACTIONS.CLOSE],
  }),
  badGateway: (error) => ({
    title: 'Service Error',
    message:
      error.message ||
      'An upstream service is temporarily unavailable. Please try again in a moment.',
    actions: [ERROR_ACTIONS.RETRY, ERROR_ACTIONS.REPORT, ERROR_ACTIONS.CLOSE],
  }),
  misconfigured: () => ({
    title: 'Configuration Error',
    message:
      'The requested resource has a misconfiguration. Please contact IT Support.',
    actions: [ERROR_ACTIONS.REPORT, ERROR_ACTIONS.CLOSE],
  }),
  default: () => ({
    title: 'Something went wrong',
    message:
      'An unexpected error occurred. Please try again or contact support if the issue persists.',
    actions: [ERROR_ACTIONS.REPORT, ERROR_ACTIONS.CLOSE],
  }),
};

/**
 * Display info generator
 * (normalize first, then map to the same UX you had)
 */
export const getErrorDisplayInfo = (incoming) => {
  // Preserve your behavior: support raw RTKQ/TypeError/etc.
  const e =
    typeof incoming === 'object' ? incoming : {message: String(incoming)};

  // If it's already one of your typed errors, keep using message/status
  // Else, extract useful message first (DRF/RTKQ) for better UX
  const normalizedMsg = e?.data?.detail || e?.data?.error || e?.message || '';

  let status = e?.status || e?.originalStatus || e?.originalError?.status;
  let name = e?.name;
  let messageText = normalizedMsg;

  // Non-typed errors: infer better messages & status
  if (!name || !name.includes('Error')) {
    const parsed = parseError(e);
    status = parsed?.status ?? status;
    messageText = parsed?.data?.detail ?? messageText;
    name = parsed?.name || name;
  }

  const error = {...e, message: messageText, status};

  // Keep your original status/name mapping for parity
  if (name === 'AuthError') {
    const authType = error.authType || 'authentication';
    return strategies[authType]?.(error) || strategies.authentication();
  } else if (name === 'TrackError') {
    return strategies[error.type]?.(error) || strategies.default();
  }

  switch (status) {
    case 401: {
      const authMsgs = [
        'Invalid Token',
        'Authentication credentials were not provided.',
      ];
      if (authMsgs.some((m) => messageText?.includes(m)))
        return strategies.authentication();
      return strategies.authorization(error);
    }
    case 403:
      return strategies.permissionDenied();
    case 404:
      return strategies.notFound(error);
    case 199:
      return strategies.misconfigured();
    case 500:
      if (messageText === 'Not Found') return strategies.notFound(error);
      if (messageText === 'Permission Denied')
        return strategies.permissionDenied();
      if (messageText === 'Unauthorized')
        return strategies.authorization(error);
      if (messageText === 'Invalid Token') return strategies.authentication();
      return strategies.serverError();
    case 502:
      return strategies.badGateway(error);
    default:
      if (status >= 500) return strategies.serverError();
      if (status >= 400) return strategies.notFound(error);
      // `name` (not error.name): spreading an Error into `error` drops it.
      if (
        name === 'NetworkError' ||
        (name === 'TypeError' && FETCH_FAILURE_MESSAGE_RE.test(messageText))
      )
        return strategies.networkError();
      return strategies.default();
  }
};

/**
 * Action handlers for dialogs
 */
export const createErrorActionHandlers = (options = {}) => {
  const {onClose = () => {}, customHandlers = {}} = options;
  const rum = getDatadogRum();

  return {
    [ERROR_ACTIONS.LOGIN]: () => {
      onClose();
      openLoginPopup({fallbackMode: 'blocked'});
    },
    [ERROR_ACTIONS.HOME]: () => {
      localStorage.setItem('redirect', window.location.pathname);
      onClose();
      window.location.href = '/';
      if (rum) rum.addAction('redirect', {location: window.location.href});
    },
    [ERROR_ACTIONS.BACK]: () => {
      window.history.back();
      onClose();
    },
    [ERROR_ACTIONS.CLOSE]: () => {
      onClose();
    },
    [ERROR_ACTIONS.REPORT]: async () => {
      if (customHandlers.report) await customHandlers.report();
      onClose();
    },
    [ERROR_ACTIONS.RETRY]: () => {
      if (customHandlers.retry) customHandlers.retry();
      else window.location.reload();
      onClose();
    },
    ...customHandlers,
  };
};

/**
 * Main UI handler for arbitrary errors
 * @param {*} error
 * @param {Object} [options]
 * @param {string} [options.key] - Override the message key (useful for programmatic cleanup via message.destroy(key))
 */
export const handleApiError = (error, {key: keyOverride} = {}) => {
  const user = selectUserContext(store.getState());
  const trackError = createTrackError(error);
  const err = getErrorDisplayInfo(trackError);
  const {title, message: msg, actions} = err;
  // Session-expiry (useTokenRefresh) and unauthenticated (ProtectedRoute) UX
  // can both fire for the same underlying session loss. Share a key so the
  // second call replaces the first instead of stacking a duplicate alert.
  const isAuthPrompt =
    trackError.name === 'AuthError' &&
    (trackError.authType === 'authentication' ||
      trackError.authType === 'unauthenticated');
  const key = keyOverride ?? (isAuthPrompt ? SESSION_EXPIRED_MESSAGE_KEY : msg);

  const contactIT = async () => {
    const payload = trackError.toJSON ? trackError.toJSON() : trackError;
    const email = await buildSupportEmailMessage({
      subject: 'TRACK Error',
      user,
      error: payload,
    });
    sendSupportEmail(email);
    message.destroy(key);
  };

  const handlers = createErrorActionHandlers({
    onClose: () => message.destroy(key),
    customHandlers: {[ERROR_ACTIONS.REPORT]: contactIT},
  });

  const ActionButtons = (xs) =>
    xs
      .map((act) => {
        const h = handlers[act];
        if (!h) return null;
        const btnProps = {
          onClick: h,
          ...(act === ERROR_ACTIONS.LOGIN ? {type: 'primary'} : {}),
        };
        return (
          <Button key={act} {...btnProps}>
            {getActionLabel(act)}
          </Button>
        );
      })
      .filter(Boolean);

  const ErrorDetails = () => (
    <div
      style={{
        fontSize: 12,
        maxHeight: '70vh',
        overflow: 'auto',
        textAlign: 'left',
        width: '100%',
      }}
    >
      <div>
        <strong>Error Type:</strong> {trackError?.name || 'Unknown'}
      </div>
      <div>
        <strong>URL:</strong> {trackError?.url || 'Unknown'}
      </div>
      <div>
        <strong>Method:</strong> {trackError?.method || 'Unknown'}
      </div>
      <div>
        <strong>Location:</strong> {window.location.href}
      </div>
      <div>
        <strong>Status:</strong> {trackError?.status || 'Unknown'}
      </div>
      <div>
        <strong>Timestamp:</strong>{' '}
        {trackError?.timestamp || new Date().toISOString()}
      </div>
      <div>
        <strong>User:</strong> {user?.email || 'Unknown'}
      </div>
      <div>
        <strong>OL ID:</strong> {user?.sub || 'Unknown'}
      </div>
      <div>
        <strong>PS Site Codes:</strong>{' '}
        {user?.ps_site_codes?.join(', ') || 'Unknown'}
      </div>
      <div>
        <strong>Permissions:</strong>{' '}
        <pre style={{display: 'inline', marginLeft: 8}}>
          {JSON.stringify(user?.permissions || {}, null, 2)}
        </pre>
      </div>
      <div>
        <strong>Message:</strong>{' '}
        <pre style={{display: 'inline', marginLeft: 8}}>
          {trackError?.message || msg}
        </pre>
      </div>
      {trackError?.toJSON && (
        <div>
          <strong>Full Error:</strong>
          <pre style={{display: 'inline', marginLeft: 8}}>
            {JSON.stringify(trackError.toJSON(), null, 2)}
          </pre>
        </div>
      )}
    </div>
  );

  message.warning({
    content: (
      <div style={{maxWidth: 600, width: '100%'}}>
        <div>
          <h3 style={{margin: '0 0 8px 0'}}>{title}</h3>
          <p style={{margin: '0 0 16px 0', wordWrap: 'break-word'}}>{msg}</p>
          {actions?.length > 0 && <Space>{ActionButtons(actions)}</Space>}
        </div>
        <Collapse
          ghost
          style={{marginTop: 16}}
          items={[
            {
              key: '1',
              label: 'Details',
              children: <ErrorDetails />,
              style: {maxHeight: '80vh', width: '100%', textAlign: 'start'},
            },
          ]}
        />
      </div>
    ),
    duration: 0,
    key,
  });
};

/**
 * RTK Query error handler
 */
export const handleRTKQError = (error, meta) => {
  const normalized = parseError(error);
  const requestMeta = extractRequestMeta(meta);
  const trackError = createTrackError(normalized, {
    url: requestMeta.request?.url,
    method: requestMeta.request?.method,
    status: normalized.status || requestMeta.response?.status,
  });

  errorLogger.logApiError(trackError, requestMeta);
  const status = normalized.status || normalized.originalStatus;
  if (status !== 502) {
    handleApiError(trackError);
  }
  return normalized;
};
